/** importing libraries **/
import axios from "axios";
import jwtDecode from "jwt-decode";

/** importing dependencies **/
import { ILocalStorage } from "../interfaces/interfaces";
import { store } from "../redux/store";
import { loaduser } from "../redux/auth/auth.slice";

class AuthUtils {
    static setLocalStorage<T extends keyof ILocalStorage>(
        key: T,
        value: ILocalStorage[T]
    ): void {
        localStorage.setItem(key, JSON.stringify(value));
    }

    static getLocalStorage<T extends keyof ILocalStorage>(
        key: T
    ): ILocalStorage[T] | null {
        const value = localStorage.getItem(key);

        if (value) {
            return JSON.parse(value);
        }

        return null;
    }

    static removeLocalStorage<T extends keyof ILocalStorage>(key: T): void {
        localStorage.removeItem(key);
    }

    static clearAuthToken(): void {
        delete axios.defaults.headers.common["Authorization"];
        delete axios.defaults.headers.common["x-invitation-token"];
    }

    static setAuthToken(): void {
        const token = this.getLocalStorage("access_token");
        if (token) {
            axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
            // If anonymous token, also set invitation token header if available
            const invitationToken = localStorage.getItem('invitation_token') || 
                                   new URLSearchParams(window.location.search).get('token');
            if (invitationToken && this.isAnonymousToken(token)) {
                axios.defaults.headers.common["x-invitation-token"] = invitationToken;
            }
        } else {
            this.clearAuthToken();
        }
    }

    /**
     * Check if a token is an anonymous invitation token
     * Anonymous tokens have 'type: anonymous' in their payload
     */
    static isAnonymousToken(token: string): boolean {
        try {
            const decoded = jwtDecode<any>(token);
            return decoded?.type === 'anonymous';
        } catch {
            return false;
        }
    }

    /**
     * Get headers with proper Authorization and invitation token if needed
     * This should be used by Redux slices to ensure anonymous tokens work
     */
    static getHeaders(accessToken?: string | null): any {
        const token = accessToken || this.getLocalStorage("access_token");
        if (!token) {
            return {};
        }

        const headers: any = {
            Authorization: `Bearer ${token}`,
        };

        // If anonymous token, include invitation token header
        if (this.isAnonymousToken(token)) {
            const invitationToken = localStorage.getItem('invitation_token') || 
                                   new URLSearchParams(window.location.search).get('token');
            if (invitationToken) {
                headers['x-invitation-token'] = invitationToken;
            }
        }

        return headers;
    }

    static async authorize(_store: typeof store): Promise<void> {
        const token = this.getLocalStorage("access_token");
        let userId;

        if (token) {
            const decodedToken = jwtDecode<{ _id: string }>(token);
            if ("_id" in decodedToken) {
                userId = decodedToken._id;
            }
        }

        try {
            if (token && userId) {
                _store.dispatch(loaduser(userId));
            } else {
                return;
            }
        } catch (error: any) {
            console.log(error.message, "error");
        }
    }

    /**
     * Safely extract token from localStorage
     * Handles both JSON string and plain string formats
     * Removes quotes if present
     */
    static getAuthToken(): string {
        try {
            const tokenRaw = localStorage.getItem('access_token');
            if (!tokenRaw) return '';
            // Try to parse as JSON first (in case it's stored as JSON string)
            try {
                const parsed = JSON.parse(tokenRaw);
                return typeof parsed === 'string' ? parsed : tokenRaw;
            } catch {
                // If parsing fails, it's already a plain string
                // Remove quotes if present
                return tokenRaw.replace(/^["']|["']$/g, '');
            }
        } catch {
            return '';
        }
    }
}

export default AuthUtils;
